import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import chatService from "../../services/chatService";
import { queryKeys } from "./queryKeys";

// Chat users (cached while on chat screen, cleared on unmount)
export const useChatUsers = (search = "", enabled = false) => {
  return useQuery({
    queryKey: queryKeys.chat.users(search),
    queryFn: () =>
      chatService.getChatUsers({
        limit: 100,
        ...(search.trim().length >= 2 && { search: search.trim() }),
      }).then((res) => res.data?.data || []),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// Conversations
export const useConversations = () => {
  return useQuery({
    queryKey: queryKeys.chat.conversations(),
    queryFn: () => chatService.getConversations(),
    staleTime: 2 * 60 * 1000,
  });
};

export const useConversation = (id) => {
  return useQuery({
    queryKey: [...queryKeys.chat.conversations(), id],
    queryFn: () => chatService.getConversationById(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
};

export const useCreateConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (participantId) =>
      chatService.createConversation(participantId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat.conversations(),
      });
    },
  });
};

// Messages
export const useMessages = (conversationId, filters = {}) => {
  return useQuery({
    queryKey: queryKeys.chat.messages(conversationId),
    queryFn: () => chatService.getMessages(conversationId, filters),
    enabled: !!conversationId,
    staleTime: 1 * 60 * 1000,
  });
};

export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, message }) =>
      chatService.sendMessage(conversationId, message),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat.messages(variables.conversationId),
      });
    },
  });
};

export const useDeleteMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, messageId }) =>
      chatService.deleteMessage(conversationId, messageId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat.messages(variables.conversationId),
      });
    },
  });
};

// Groups
export const useGroups = () => {
  return useQuery({
    queryKey: queryKeys.chat.groups(),
    queryFn: () => chatService.getGroups(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useGroup = (id) => {
  return useQuery({
    queryKey: [...queryKeys.chat.groups(), id],
    queryFn: () => chatService.getGroupById(id).then((res) => res.data?.data),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupData) => chatService.createGroup(groupData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat.groups(),
      });
    },
  });
};

export const useUpdateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, groupData }) =>
      chatService.updateGroup(id, groupData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.chat.groups(), variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat.groups(),
      });
    },
  });
};

export const useDeleteGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => chatService.deleteGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat.groups(),
      });
    },
  });
};

export const useAddGroupMembers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, memberIds }) =>
      chatService.addGroupMembers(groupId, memberIds),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.chat.groups(), variables.groupId],
      });
    },
  });
};

export const useRemoveGroupMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, memberId }) =>
      chatService.removeGroupMember(groupId, memberId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.chat.groups(), variables.groupId],
      });
    },
  });
};

// Group Messages
export const useGroupMessages = (groupId, filters = {}) => {
  return useQuery({
    queryKey: queryKeys.chat.groupMessages(groupId),
    queryFn: () => chatService.getGroupMessages(groupId, filters),
    enabled: !!groupId,
    staleTime: 1 * 60 * 1000,
  });
};

export const useSendGroupMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, message }) =>
      chatService.sendGroupMessage(groupId, message),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat.groupMessages(variables.groupId),
      });
    },
  });
};

// Community Groups
export const useCommunityGroups = () => {
  return useQuery({
    queryKey: [...queryKeys.chat.groups(), "community"],
    queryFn: () => chatService.getCommunityGroups(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCommunityGroup = (id) => {
  return useQuery({
    queryKey: [...queryKeys.chat.groups(), "community", id],
    queryFn: () => chatService.getCommunityGroupById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateCommunityGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupData) => chatService.createCommunityGroup(groupData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.chat.groups(), "community"],
      });
    },
  });
};

export const useJoinCommunityGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId) => chatService.joinCommunityGroup(groupId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.chat.groups(), "community", variables],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.chat.groups(), "community"],
      });
    },
  });
};

export const useLeaveCommunityGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId) => chatService.leaveCommunityGroup(groupId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.chat.groups(), "community", variables],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.chat.groups(), "community"],
      });
    },
  });
};

// Broadcast Groups
export const useBroadcastGroups = () => {
  return useQuery({
    queryKey: queryKeys.broadcast.groups(),
    queryFn: () => chatService.getBroadcastGroups(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useBroadcastGroup = (id) => {
  return useQuery({
    queryKey: [...queryKeys.broadcast.groups(), id],
    queryFn: () => chatService.getBroadcastGroupById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateBroadcastGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupData) => chatService.createBroadcastGroup(groupData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.broadcast.groups(),
      });
    },
  });
};

export const useSendBroadcastMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, message }) =>
      chatService.sendBroadcastMessage(groupId, message),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.broadcast.all,
      });
    },
  });
};

export const useSubscribeToBroadcast = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId) => chatService.subscribeToBroadcast(groupId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.broadcast.groups(), variables],
      });
    },
  });
};

export const useUnsubscribeFromBroadcast = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId) => chatService.unsubscribeFromBroadcast(groupId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.broadcast.groups(), variables],
      });
    },
  });
};

